import React, { useMemo } from 'react';
import { StyledRichTextAdvancedText } from '../../rich-text/style/StyledRichTextAdvancedText.tsx';
import {
  type IAdvancedRichText,
  type IAdvancedRichTextItem,
} from '../IAdvancedRichText.ts';
import { postprocessRichTextAdvancedHtml } from './postprocess-html/postprocessRichTextAdvancedHtml.tsx';
import { RichTextConditional } from './RichTextConditional.tsx';
import { toAdvancedRichText } from './toAdvancedRichText.ts';

export interface IRichTextAdvancedRenderProps {
  text?: IAdvancedRichText | string | null;
  /** Flag per indicare se i paragrafi annidati devono avere una formattazione inline*/
  inline?: boolean;
  /** Flag che indica se l'elemento renderizzato deve essere stilizzato */
  hasCustomFont?: boolean;
}

/**
 * Rileva se il rich text è "semplice": un solo item di tipo 'text' senza
 * regole condizionali e senza marker speciali (tooltip, reference).
 * Per questi casi, si evita completamente il pipeline di postprocessing
 * e html-react-parser, usando direttamente dangerouslySetInnerHTML.
 */
function isSimpleRichText(
  items: IAdvancedRichText,
): items is [IAdvancedRichTextItem & { text: string }] {
  if (items.length !== 1) return false;
  const item = items[0];
  if (item.type !== 'text') return false;
  if (item.rule) return false;
  if (!item.text) return false;
  if (item.text.includes('data-type="tooltip-virtual-node"')) return false;
  if (item.text.includes('data-type="compute-expression-reference"'))
    return false;
  // I link richiedono il rendering completo per l'enhancement a11y (sr-only)
  if (item.text.includes('<a ')) return false;
  return true;
}

/**
 * Renderizza in modo centralizzato i testi avanzati (creati con l'editor
 * RTE avanzato).
 *
 * Utilizza un fast-path per i testi semplici (la maggior parte dei label)
 * che evita il postprocessing e il parsing HTML, e un slow-path per i
 * casi complessi (info icon, reference, regole condizionali).
 */
export const RichTextAdvancedRender = React.memo(
  function RichTextAdvancedRender(props: IRichTextAdvancedRenderProps) {
    const { inline, hasCustomFont, text } = props;

    const normalized = useMemo(
      () => (text ? toAdvancedRichText(text) : null),
      [text],
    );

    if (!normalized || normalized.every(item => !item.text)) return null;

    // FAST PATH: semplice HTML senza info, rules, o reference.
    // Evita postprocessRichTextAdvancedHtml, RichTextConditional,
    // html-react-parser e useExpressionValue hook chain.
    if (isSimpleRichText(normalized)) {
      return (
        <StyledRichTextAdvancedText
          inline={inline}
          hasCustomFont={hasCustomFont}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML proveniente dalla configurazione widget (editor RTE admin), non da input utente
          dangerouslySetInnerHTML={{ __html: normalized[0].text }}
        />
      );
    }

    // SLOW PATH: postprocessing completo per casi complessi
    const items = postprocessRichTextAdvancedHtml(normalized);
    return (
      <span>
        {items.map((item, index) => (
          <RichTextConditional
            key={index}
            item={item}
            index={index}
            inline={inline}
            isInfoIcon={item.type === 'info'}
            hasCustomFont={hasCustomFont}
          />
        ))}
      </span>
    );
  },
);
